import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Paperclip, X, FileText } from "lucide-react";
import { EMAIL } from "../lib/site";
import Seo from "../components/Seo";
import { businessSchema } from "../lib/seoSchemas";
import { useContent } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const { t } = useContent();
  const [form, setForm] = useState({ name: "", email: "", background: "", message: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Resume must be a PDF, DOC, or DOCX file.");
      e.target.value = "";
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Resume must be 5MB or smaller.");
      e.target.value = "";
      return;
    }
    setResumeFile(f);
  };

  const clearResume = () => {
    setResumeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      let resumeId = null;
      if (resumeFile) {
        const fd = new FormData();
        fd.append("file", resumeFile);
        const up = await axios.post(`${API}/careers/resume`, fd);
        resumeId = up.data?.resume_id || null;
      }
      const res = await axios.post(`${API}/careers`, { ...form, resume_id: resumeId });
      toast.success(res.data?.message || "Thanks. We will be in touch.");
      setForm({ name: "", email: "", background: "", message: "" });
      clearResume();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-careers">
      <Seo
        title={t("careers.seo.title")}
        description={t("careers.seo.description")}
        schemas={[businessSchema]}
      />
      {/* HERO */}
      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="careers-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">{t("careers.hero.eyebrow")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight max-w-4xl">
            {t("careers.hero.heading")}
          </h1>
          <p className="mt-8 text-lg text-ink-soft max-w-2xl leading-relaxed">
            {t("careers.hero.intro")}
          </p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="careers-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="eyebrow mb-4">Who We're Looking For</div>
              <h2 className="font-serif text-3xl text-brand-700 leading-tight mb-6">
                {t("careers.aside.heading")}
              </h2>
              <p className="text-ink-soft leading-relaxed mb-8">
                {t("careers.aside.body")}
              </p>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-brand-700 mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-1">
                    Or email directly
                  </div>
                  <a
                    href={`mailto:${EMAIL}?subject=Careers%20Inquiry`}
                    className="text-ink-soft hover:text-brand-700 break-all"
                    data-testid="careers-email-link"
                  >
                    {EMAIL}
                  </a>
                </div>
              </div>
            </aside>

            <form
              className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-ink-line"
              onSubmit={handleSubmit}
              data-testid="careers-form"
            >
              <div className="eyebrow mb-4">{t("careers.form.eyebrow")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="careers-input-name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="careers-input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Background (current role, years of experience, focus)
                  </label>
                  <input
                    type="text"
                    name="background"
                    value={form.background}
                    onChange={handleChange}
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="careers-input-background"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    What kind of work are you looking for?
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors resize-y"
                    data-testid="careers-input-message"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Resume (optional, PDF or Word, max 5MB)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="careers-input-resume"
                  />
                  {resumeFile ? (
                    <div
                      className="flex items-center justify-between gap-3 border border-brand-200 bg-brand-100/60 px-4 py-3"
                      data-testid="careers-resume-selected"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-brand-700 shrink-0" />
                        <span className="text-sm text-ink-soft truncate">{resumeFile.name}</span>
                        <span className="text-xs text-ink-muted shrink-0">
                          {(resumeFile.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={clearResume}
                        className="text-ink-muted hover:text-red-600 transition-colors shrink-0"
                        aria-label="Remove resume"
                        data-testid="careers-resume-remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 border border-ink-line text-ink-soft hover:border-brand-700 hover:text-brand-700 px-4 py-3 text-sm transition-colors"
                      data-testid="careers-resume-attach-btn"
                    >
                      <Paperclip className="w-4 h-4" /> Attach Resume
                    </button>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-sm font-medium transition-colors"
                data-testid="careers-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    {t("careers.form.submit")} <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
